import type { Permission } from '@fieldvision/contracts';
import { canAny, useAccessScope, useAuth, useSessionBootstrap } from '@fieldvision/web-core';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Suspense, lazy, type ReactNode } from 'react';

import { ErrorBoundary } from './components/ErrorBoundary.js';
import { Shell } from './components/shell/Shell.js';
import { CONSOLE_PERMISSIONS, NAV_ITEMS } from './components/shell/nav.js';
import { Banner, Panel, Spinner } from './components/ui/index.js';
import { LoginPage } from './pages/LoginPage.js';
import { OverviewPage } from './pages/overview/OverviewPage.js';
import { resolveStaffUrl } from './runtime-config.js';

/** Optional — when unset the cross-link is simply omitted rather than broken. */
const STAFF_URL = resolveStaffUrl();

/*
 * Every route except the landing page is code-split.
 *
 * `OverviewPage` stays eager: it is the landing route for every role holding
 * `dashboard:read`, so lazy-loading it would add a round trip to the screen
 * everyone sees first and split nothing off the critical path. `LoginPage` is
 * eager for the same reason from the other direction.
 *
 * The rest — eight master-data editors, the users screens, the report builder —
 * are pages a manager visits occasionally and paid for on every load. Splitting
 * them is what keeps the entry chunk under Vite's 500 kB warning now that
 * Recharts is in the tree; the warning is left armed on purpose (see
 * `vite.config.ts`).
 */
const FraudQueuePage = lazy(() =>
  import('./pages/FraudQueuePage.js').then((module) => ({ default: module.FraudQueuePage })),
);
const PlanogramPage = lazy(() =>
  import('./pages/planogram/PlanogramPage.js').then((module) => ({
    default: module.PlanogramPage,
  })),
);
const PlanogramVisitsPage = lazy(() =>
  import('./pages/planogram/PlanogramVisitsPage.js').then((module) => ({
    default: module.PlanogramVisitsPage,
  })),
);
const PriceIndexPage = lazy(() =>
  import('./pages/price-index/PriceIndexPage.js').then((module) => ({
    default: module.PriceIndexPage,
  })),
);
const PriceVisitsPage = lazy(() =>
  import('./pages/price-index/PriceVisitsPage.js').then((module) => ({
    default: module.PriceVisitsPage,
  })),
);
const ReportsPage = lazy(() =>
  import('./pages/reports/ReportsPage.js').then((module) => ({ default: module.ReportsPage })),
);
const DownloadsPage = lazy(() =>
  import('./pages/downloads/DownloadsPage.js').then((module) => ({ default: module.DownloadsPage })),
);
const CompetitorsPage = lazy(() =>
  import('./pages/master/CompetitorsPage.js').then((module) => ({ default: module.CompetitorsPage })),
);
const ChannelsPage = lazy(() =>
  import('./pages/master/ChannelsPage.js').then((module) => ({
    default: module.ChannelsPage,
  })),
);
const ClassificationsPage = lazy(() =>
  import('./pages/master/ClassificationsPage.js').then((module) => ({
    default: module.ClassificationsPage,
  })),
);
const SkuCategoriesPage = lazy(() =>
  import('./pages/master/SkuCategoriesPage.js').then((module) => ({
    default: module.SkuCategoriesPage,
  })),
);
const SkuGroupingsPage = lazy(() =>
  import('./pages/master/SkuGroupingsPage.js').then((module) => ({
    default: module.SkuGroupingsPage,
  })),
);
const ImportPage = lazy(() =>
  import('./pages/master/ImportPage.js').then((module) => ({ default: module.ImportPage })),
);
const OutletsPage = lazy(() =>
  import('./pages/master/OutletsPage.js').then((module) => ({ default: module.OutletsPage })),
);
const RegionsPage = lazy(() =>
  import('./pages/master/RegionsPage.js').then((module) => ({ default: module.RegionsPage })),
);
const SkusPage = lazy(() =>
  import('./pages/master/SkusPage.js').then((module) => ({ default: module.SkusPage })),
);
const VisitTargetsPage = lazy(() =>
  import('./pages/master/VisitTargetsPage.js').then((module) => ({ default: module.VisitTargetsPage })),
);
const CompetitionPlansPage = lazy(() =>
  import('./pages/master/CompetitionPlansPage.js').then((module) => ({
    default: module.CompetitionPlansPage,
  })),
);
const SettingsPage = lazy(() =>
  import('./pages/settings/SettingsPage.js').then((module) => ({ default: module.SettingsPage })),
);
const RolePermissionsPage = lazy(() =>
  import('./pages/users/RolePermissionsPage.js').then((module) => ({ default: module.RolePermissionsPage })),
);
const UsersPage = lazy(() =>
  import('./pages/users/UsersPage.js').then((module) => ({ default: module.UsersPage })),
);
const SystemHealthPage = lazy(() =>
  import('./pages/system/SystemHealthPage.js').then((module) => ({
    default: module.SystemHealthPage,
  })),
);
const PhotoFailuresPage = lazy(() =>
  import('./pages/system/PhotoFailuresPage.js').then((module) => ({
    default: module.PhotoFailuresPage,
  })),
);

/**
 * The manager console's routing — the SIGNED-IN half of the application.
 *
 * `App.tsx` decides whether this is mounted at all. Everything under `/legal/*`
 * is public and never reaches here, which is why the session gates below can
 * stay exactly as unconditional as they look.
 *
 * Route access is driven by `GET /auth/me` rather than by the role string in the
 * session, because the role→permission matrix is editable at runtime through
 * this very application — a client-side role check would go stale the moment an
 * administrator used the screen at `/users/roles`.
 *
 * As everywhere: this decides what to OFFER. Every endpoint behind these routes
 * is guarded by the API's global AuthGuard and row-scoped independently
 * (TSP 2.2.2), so a user who edits the URL gets a 403 from the server, not data.
 */
export function ConsoleApp() {
  const bootstrapped = useSessionBootstrap();
  const { pathname } = useLocation();
  const accessToken = useAuth((state) => state.accessToken);
  const scope = useAccessScope();

  /*
   * "No access token" is not yet "not signed in" — the refresh cookie outlives
   * the token this tab lost, so a new tab or a reopened browser still holds a
   * session the console has not asked for yet. Wait for the answer before
   * branching, or a signed-in manager is shown a login form.
   */
  if (!bootstrapped) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Spinner label="Memulihkan sesi…" />
      </div>
    );
  }

  if (!accessToken) return <LoginPage />;

  if (scope.isPending) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Spinner label="Memuat izin…" />
      </div>
    );
  }

  // The scope call failed, so we cannot know what to offer. Rendering an empty
  // shell would read as "you have no access"; this says what actually happened.
  if (scope.isError) {
    return (
      <div className="flex min-h-dvh items-center justify-center p-gutter">
        <div className="max-w-md">
          <Banner tone="error" live>
            Gagal memuat izin akses. Periksa koneksi lalu muat ulang halaman.
          </Banner>
        </div>
      </div>
    );
  }

  /*
   * Console membership first, landing page second.
   *
   * These are two different questions and conflating them is what let a SALES
   * user in: they hold `outlet:read`, so the first nav item they could see was
   * the master-data outlet browser, so "has a landing page" answered yes.
   */
  const belongsHere = canAny(scope.data, CONSOLE_PERMISSIONS);
  const landing = NAV_ITEMS.find((item) => canAny(scope.data, item.any));

  if (!belongsHere || !landing) {
    return (
      <div className="flex min-h-dvh items-center justify-center p-gutter">
        <div className="surface max-w-md p-6">
          <h1 className="text-lg">Tidak ada akses</h1>
          <p className="mt-2 text-sm text-muted">
            Akun ini tidak memiliki izin untuk Konsol Manajer. Gunakan aplikasi lapangan untuk
            mencatat kunjungan.
          </p>
          {STAFF_URL ? (
            <a className="mt-4 inline-block text-sm font-semibold text-primary-700 underline" href={STAFF_URL}>
              Buka aplikasi lapangan
            </a>
          ) : null}
          <button
            type="button"
            className="focus-ring mt-4 block text-sm text-muted underline"
            onClick={() => useAuth.getState().signOut()}
          >
            Keluar
          </button>
        </div>
      </div>
    );
  }

  return (
    <Shell scope={scope.data}>
      {/*
        THE SECOND BOUNDARY, INSIDE THE SHELL.

        `main.tsx` has one around the whole application, which is what stops a
        thrown error from emptying the document. This one is about what the user
        can do next: a report that throws costs the content pane, and the sidebar
        stays there to navigate away with — rather than a full-page apology whose
        only exit is a reload.

        KEYED ON THE PATHNAME because React error boundaries do not reset
        themselves. Without the key, one bad screen would keep its fallback for
        the rest of the session and every subsequent navigation would land on the
        error from the page before. Changing the key remounts the boundary, which
        is the documented way to clear one.
      */}
      <ErrorBoundary key={pathname}>
      {/*
        The fallback lives INSIDE the shell, so a route change swaps the content
        pane and leaves the sidebar and top bar in place. Wrapping the shell
        instead would blink the whole chrome on every navigation, which reads as
        a full page reload.
      */}
      <Suspense
        fallback={
          <Panel>
            <Spinner label="Memuat halaman…" />
          </Panel>
        }
      >
      <Routes>
        {/*
          Each route repeats the permission its sidebar entry declares in
          `nav.ts`. Duplication rather than derivation, because a typed URL never
          touches the sidebar — a route guarded only by whether its link renders
          is not guarded at all.
        */}
        <Route path="/overview" element={<Guard need={['dashboard:read']} fallback={landing.to}><OverviewPage /></Guard>} />
        <Route path="/reports" element={<Guard need={['report:read', 'dashboard:read']} fallback={landing.to}><ReportsPage /></Guard>} />
        <Route path="/price-index" element={<Guard need={['report:read']} fallback={landing.to}><PriceIndexPage /></Guard>} />
        {/*
          A nested PATH but a sibling ROUTE, deliberately. React Router v6 ranks
          by specificity rather than declaration order, so this wins over
          `/price-index` without a layout route between them — and the two pages
          share nothing but a URL prefix, so a shared layout would be an outlet
          with one child.
        */}
        <Route path="/price-index/visits" element={<Guard need={['report:read']} fallback={landing.to}><PriceVisitsPage /></Guard>} />
        <Route path="/planogram" element={<Guard need={['report:read']} fallback={landing.to}><PlanogramPage /></Guard>} />
        {/* A sibling route under a nested path, for the reason above. */}
        <Route path="/planogram/visits" element={<Guard need={['report:read']} fallback={landing.to}><PlanogramVisitsPage /></Guard>} />
        <Route path="/fraud" element={<Guard need={['fraud:read']} fallback={landing.to}><FraudQueuePage /></Guard>} />
        {/* `report:export` alone: the one screen whose every endpoint is that permission. */}
        <Route path="/downloads" element={<Guard need={['report:export']} fallback={landing.to}><DownloadsPage /></Guard>} />

        <Route path="/master/outlets" element={<Guard need={['outlet:read']} fallback={landing.to}><OutletsPage /></Guard>} />
        <Route path="/master/regions" element={<Guard need={['outlet:read']} fallback={landing.to}><RegionsPage /></Guard>} />
        <Route path="/master/skus" element={<Guard need={['sku:read']} fallback={landing.to}><SkusPage /></Guard>} />
        <Route path="/master/channels" element={<Guard need={['outlet:read']} fallback={landing.to}><ChannelsPage /></Guard>} />
        <Route path="/master/classifications" element={<Guard need={['outlet:read']} fallback={landing.to}><ClassificationsPage /></Guard>} />
        <Route path="/master/sku-categories" element={<Guard need={['sku:read']} fallback={landing.to}><SkuCategoriesPage /></Guard>} />
        <Route path="/master/sku-groupings" element={<Guard need={['sku:read']} fallback={landing.to}><SkuGroupingsPage /></Guard>} />
        <Route path="/master/competitors" element={<Guard need={['sku:read']} fallback={landing.to}><CompetitorsPage /></Guard>} />
        <Route path="/master/visit-targets" element={<Guard need={['target:read']} fallback={landing.to}><VisitTargetsPage /></Guard>} />
        <Route path="/master/competition-plans" element={<Guard need={['target:read']} fallback={landing.to}><CompetitionPlansPage /></Guard>} />
        <Route path="/master/import" element={<Guard need={['masterdata:import']} fallback={landing.to}><ImportPage /></Guard>} />

        <Route path="/settings" element={<Guard need={['target:read']} fallback={landing.to}><SettingsPage /></Guard>} />
        {/* `/users/roles` before `/users` — otherwise the shorter path wins. */}
        <Route path="/users/roles" element={<Guard need={['user:read']} fallback={landing.to}><RolePermissionsPage /></Guard>} />
        <Route path="/users" element={<Guard need={['user:read']} fallback={landing.to}><UsersPage /></Guard>} />
        {/* The longer path first, for the reason `/users/roles` states above. */}
        <Route path="/system/photo-failures" element={<Guard need={['system:read']} fallback={landing.to}><PhotoFailuresPage /></Guard>} />
        <Route path="/system" element={<Guard need={['system:read']} fallback={landing.to}><SystemHealthPage /></Guard>} />

        <Route path="*" element={<Navigate to={landing.to} replace />} />
      </Routes>
      </Suspense>
      </ErrorBoundary>
    </Shell>
  );
}

/**
 * Redirects rather than renders when the permission is absent.
 *
 * Redirect and not a "forbidden" page: the user did not choose this route, the
 * app did — they either typed a URL or followed a stale bookmark, and dropping
 * them somewhere they can actually work is more useful than an apology.
 */
function Guard({ need, fallback, children }: { need: Permission[]; fallback: string; children: ReactNode }) {
  const scope = useAccessScope();
  if (!canAny(scope.data, need)) return <Navigate to={fallback} replace />;
  return <>{children}</>;
}
